import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { INotificationDefinitionDto } from 'api/api';
import warning from 'assets/warning-icon.svg';
import { Button } from 'primereact/button';
import React, { useCallback, useEffect, useState } from 'react';
import { reactAI } from 'services/telemetry-service';
import {
	deleteNotificationDefinition,
	getNotificationDefinitions,
	navigateToSpecificNotification,
} from 'state/ducks/notifications/operations';
import { localized } from 'state/i18n';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { isAtLeastAdminFromStateAcrossGroups, isAtLeastCountryAdminFromStateAcrossGroups } from 'utilities/roles-util';
import { Spinner } from 'view/components/spinner/spinner';
import Accordion from 'view/shared/components/bi-accordion/accordion';
import BiInputText from 'view/shared/components/bi-input-text/bi-input-text';
import BiTooltip from 'view/shared/components/bi-tooltip/bi-tooltip';
import NotificationAccordionContent from './notification-accordion-content';
import NotificationAccordionHeader from './notification-accordion-header';
import './notifications-list.scss';

const buttonClassName = 'p-button-custom bi-button-icon-only ';
export const classNameCardContainer = 'width-100-percent padding-vertical-0-5 ';

const NotificationsList: React.FC = () => {
	const dispatch = useAppDispatch();
	const notificationDefinitions = useAppSelector(state => state.notificationsReducer.notificationDefinitions);
	const sorting = useAppSelector(state =>
		state.tableSettingsReducer.tableSettingsList.find(
			ts => ts.tableSettings.id === state.tableSettingsReducer.currentTableSettingId
		)?.sorting
	);
	const loading = useAppSelector(state => state.notificationsReducer.loading);
	const appliedFilter = useAppSelector(state => state.filterSearchReducer.appliedFilters);
	const isAtleastCountryAdmin = useAppSelector(isAtLeastCountryAdminFromStateAcrossGroups);
	const isAtLeastAdmin = useAppSelector(isAtLeastAdminFromStateAcrossGroups);

	const [userSearchFilter, setUserSearchFilter] = useState('');

	useEffect(() => {
		getNotificationDefinitions(appliedFilter)(dispatch);
	}, [appliedFilter, dispatch]);

	const handleClickDeleteBtn = useCallback(
		(def: INotificationDefinitionDto) => {
			deleteNotificationDefinition(def)(dispatch);
		},
		[dispatch]
	);

	const handleClickEditBtn = useCallback(
		(def: INotificationDefinitionDto) => {
			navigateToSpecificNotification(def.id, true)(dispatch);
		},
		[dispatch]
	);

	const buttonsGenerator = useCallback(
		(
			def: INotificationDefinitionDto,
			handleEdit: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
			handleDelete: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
		): JSX.Element => {
			return (
				<div className="flex-1 text-right">
					{def.hasOfflineMachines && (
						<BiTooltip overlay={localized('UsedByOfflineMachines')} noNesting={true}>
							<img src={warning} alt="warning" className="warning-icon" />
						</BiTooltip>
					)}
					<Button
						tooltip={def.isEditAllowed ? localized('Edit') : localized('PermissionRequired')}
						className={buttonClassName}
						icon="pi pi-pencil"
						onClick={handleEdit}
						disabled={!def.isEditAllowed}
					/>
					<Button
						tooltip={def.isEditAllowed ? localized('Delete') : localized('PermissionRequired')}
						className={buttonClassName}
						icon="pi pi-trash"
						onClick={handleDelete}
						disabled={!def.isEditAllowed}
					/>
				</div>
			);
		},
		[]
	);

	const filterLower = userSearchFilter.trim().toLowerCase();

	const filteredDefinitions = filterLower
		? notificationDefinitions.filter(def =>
			def.notificationUsers?.some(user => {
				const email = user.email?.toLowerCase();
				if (!email) return false;
				let emailIndex = 0;
				for (let i = 0; i < filterLower.length; i++) {
					emailIndex = email.indexOf(filterLower[i], emailIndex);
					if (emailIndex === -1) return false;
					emailIndex++;
				}
				return true;
			})
		)
		: notificationDefinitions;

	const defs = filteredDefinitions.map(def => ({
		header: (
			<NotificationAccordionHeader
				def={def}
				buttonsGenerator={buttonsGenerator}
				handleEdit={handleClickEditBtn}
				handleDelete={handleClickDeleteBtn}
				isAtleastCountryAdmin={isAtleastCountryAdmin}
			/>
		),
		content: (
			<NotificationAccordionContent
				def={def}
				buttonsGenerator={buttonsGenerator}
				handleEdit={handleClickEditBtn}
				handleDelete={handleClickDeleteBtn}
				isAtleastCountryAdmin={isAtleastCountryAdmin}
			/>
		),
	}));

	return (
		<div className="margin-left-3 margin-right-3 margin-vertical-20px notifications-list">
			<h3>{localized('Notifications')}</h3>
			{isAtLeastAdmin && (
				<BiInputText
					icon="pi pi-search"
					placeholder={localized('FilterByUserEmail')}
					value={userSearchFilter}
					onChange={e => setUserSearchFilter(e.currentTarget.value)}
					className="user-email-filter"
				/>
			)}
			<br />

			{filteredDefinitions?.length ? (
				<>
					<div
						className={`${
							isAtleastCountryAdmin
								? 'accordion-grid-with-hidden'
								: 'accordion-grid-without-hidden'
						} padding-left-32px padding-right-32px margin-bottom-1em`}
					>
						<b className="word-break">{localized('NotificationName')}</b>
						<div className="mini-grid">
							<b className="word-break">{localized('ControllerTypes')}</b>
							<b className="word-break">{localized('Notifications')}</b>
						</div>
						<b>{localized('Users')}</b>
						{isAtleastCountryAdmin && <b>{localized('Hidden')}</b>}

						<b>{localized('CreatedBy')}</b>
						<b>{localized('LastModifiedOn')}</b>
						<b>{localized('ModifiedBy')}</b>
						<b className="text-right word-break">{localized('Action')}</b>
					</div>

					<Accordion sections={defs} />
				</>
			) : (
				loading && <Spinner shouldOverlay={false}></Spinner>
			)}
		</div>
	);
};

export default withAITracking(reactAI.reactPlugin, NotificationsList, 'NotificationsList');
